import {useMemo} from 'react';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@common-web/components/ui/accordion';
import {ChecklistStepRow} from './ChecklistStepRow';
import type {OnboardingStep} from './ChecklistStepRow';

interface OnboardingChecklistProps {
  steps: OnboardingStep[];
  onStepClick: (stepId: string) => void;
}

type PhaseKey = 'INITIAL_SETUP' | 'PRE_ENROLLMENT' | 'ENROLLMENT' | 'GO_LIVE_PREP' | 'POST_LAUNCH';

interface PhaseConfig {
  label: string;
  colorClass: string;
}

const phaseConfig: Record<PhaseKey, PhaseConfig> = {
  INITIAL_SETUP: {label: 'Initial Setup', colorClass: 'text-blue-900'},
  PRE_ENROLLMENT: {label: 'Pre-Enrollment', colorClass: 'text-blue-600'},
  GO_LIVE_PREP: {label: 'Go Live Prep', colorClass: 'text-amber-600'},
  ENROLLMENT: {label: 'Enrollment', colorClass: 'text-blue-500'},
  POST_LAUNCH: {label: 'Post Launch', colorClass: 'text-emerald-600'},
};

const phaseOrder: PhaseKey[] = [
  'INITIAL_SETUP',
  'PRE_ENROLLMENT',
  'ENROLLMENT',
  'GO_LIVE_PREP',
  'POST_LAUNCH',
];

function isKnownPhase(key: string): key is PhaseKey {
  return key in phaseConfig;
}

export function OnboardingChecklist({steps, onStepClick}: OnboardingChecklistProps) {
  const groupedPhases = useMemo(() => {
    const groups = new Map<string, OnboardingStep[]>();

    for (const step of steps) {
      const existing = groups.get(step.phaseGroup);
      if (existing) {
        existing.push(step);
      } else {
        groups.set(step.phaseGroup, [step]);
      }
    }

    // Sort steps within each group by order
    for (const [, phaseSteps] of groups) {
      phaseSteps.sort((a, b) => a.order - b.order);
    }

    // Return phases in defined order, then any unknown phases alphabetically
    const orderedKeys: string[] = [];
    for (const key of phaseOrder) {
      if (groups.has(key)) {
        orderedKeys.push(key);
      }
    }
    for (const key of groups.keys()) {
      if (!orderedKeys.includes(key)) {
        orderedKeys.push(key);
      }
    }

    return orderedKeys.map((key) => ({
      key,
      steps: groups.get(key) ?? [],
    }));
  }, [steps]);

  const defaultOpenPhases = useMemo(() => {
    const active: string[] = [];
    for (const phase of groupedPhases) {
      const hasActiveStep = phase.steps.some(
        (s) => s.status === 'IN_PROGRESS' || s.status === 'NOT_STARTED',
      );
      if (hasActiveStep) {
        active.push(phase.key);
      }
    }
    // If none are active, open the first phase
    if (active.length === 0 && groupedPhases.length > 0) {
      active.push(groupedPhases[0]!.key);
    }
    return active;
  }, [groupedPhases]);

  return (
    <Accordion type='multiple' defaultValue={defaultOpenPhases}>
      {groupedPhases.map((phase) => {
        const completedCount = phase.steps.filter(
          (s) => s.status === 'COMPLETED' || s.status === 'SKIPPED',
        ).length;
        const totalCount = phase.steps.length;
        const config = isKnownPhase(phase.key)
          ? phaseConfig[phase.key]
          : {label: phase.key, colorClass: 'text-gray-700'};

        return (
          <AccordionItem key={phase.key} value={phase.key}>
            <AccordionTrigger className='hover:no-underline'>
              <div className='flex items-center gap-3 flex-1'>
                <span className={`text-sm font-semibold ${config.colorClass}`}>{config.label}</span>
                <span className='inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600'>
                  {completedCount}/{totalCount} steps
                </span>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div className='space-y-1'>
                {phase.steps.map((step) => (
                  <ChecklistStepRow key={step.id} step={step} onClick={onStepClick} />
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        );
      })}
    </Accordion>
  );
}
